import React, { useEffect, useState } from "react";
import { ArrowUpCircle, X } from "lucide-react";
import { adminApi } from "./adminApi";

const NAVY = "#1E3A5F";
const PLAN_LABELS = { trial: "C-ORE Trial", monthly: "C-ORE Pulse", yearly: "C-ORE Command", lifetime: "C-ORE Sovereign" };
const PLAN_RANK = { trial: 0, monthly: 1, yearly: 2, lifetime: 3 };
const REASONS = [
  { value: "complimentary", label: "Complimentary access" },
  { value: "sales_intervention", label: "Sales intervention" },
  { value: "partner", label: "Partner" },
  { value: "manual_correction", label: "Manual correction" },
  { value: "other", label: "Other" },
];

const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16,
};
const card = {
  background: "white", borderRadius: 14, width: "100%", maxWidth: 480,
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden",
  fontFamily: "-apple-system,Segoe UI,Roboto,sans-serif",
};
const input = { width: "100%", boxSizing: "border-box", border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 10px", fontSize: 13, color: "#0F1B2D" };
const label = { display: "block", fontSize: 11.5, color: "#6B7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };
const btn = { border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" };

function actionLabel(currentPlan, target) {
  const name = PLAN_LABELS[target] || target;
  if (currentPlan === target) return target === "lifetime" ? `Grant ${name}` : `Renew ${name}`;
  if (target === "lifetime") return `Grant ${name}`;
  return `Upgrade to ${name}`;
}

export default function CustomerUpgradeModal({ customer, onClose, onDone }) {
  const [loadingCurrent, setLoadingCurrent] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [entitlementStatus, setEntitlementStatus] = useState(null);
  const [target, setTarget] = useState("monthly");
  const [reason, setReason] = useState("complimentary");
  const [reasonNote, setReasonNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const name = customer.company_name || customer.name || customer.id;

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await adminApi.customerBilling(customer.id);
      if (!alive) return;
      if (res.ok) {
        setCurrentPlan(res.data?.entitlement?.plan_code || null);
        setEntitlementStatus(res.data?.entitlement?.entitlement_status || "none");
      }
      setLoadingCurrent(false);
    })();
    return () => { alive = false; };
  }, [customer.id]);

  const currentRank = currentPlan in PLAN_RANK ? PLAN_RANK[currentPlan] : -1;
  const isRenewal = currentPlan === target;
  const isDowngrade = !isRenewal && PLAN_RANK[target] < currentRank;

  const confirm = async () => {
    if (isDowngrade) return;
    setBusy(true); setErr("");
    const res = await adminApi.manualUpgrade(customer.id, {
      target_plan_code: target, reason, reason_note: reason === "other" ? (reasonNote.trim() || null) : null,
    });
    setBusy(false);
    if (!res.ok) { setErr(humanize(res.error)); return; }
    setResult(res.data);
    setTimeout(() => { onDone && onDone(); }, 1100);
  };

  return (
    <div style={overlay} data-testid="upgrade-modal" onClick={() => !busy && onClose()}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #E5E7EB" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ArrowUpCircle size={16} color={NAVY} />
            <span style={{ fontWeight: 800, fontSize: 15, color: "#0F1B2D" }}>Upgrade Customer</span>
          </div>
          <button data-testid="upgrade-close" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
        </div>

        <div style={{ padding: 18 }}>
          {!result && (
            <>
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 4 }}>
                <b>{name}</b> {customer.email ? <span style={{ color: "#6B7280" }}>· {customer.email}</span> : null}
              </div>
              <div style={{ fontSize: 12.5, color: "#6B7280", marginBottom: 14 }} data-testid="upgrade-current-plan">
                Current plan: {loadingCurrent ? "loading…" : (
                  <b style={{ color: "#0F1B2D" }}>
                    {PLAN_LABELS[currentPlan] || "No active plan"}
                    {currentPlan && entitlementStatus ? ` (${entitlementStatus})` : ""}
                  </b>
                )}
              </div>

              <label style={label}>Target plan</label>
              <select data-testid="upgrade-plan-select" value={target} onChange={(e) => setTarget(e.target.value)} style={{ ...input, marginBottom: 12 }}>
                <option value="monthly">C-ORE Pulse (monthly)</option>
                <option value="yearly">C-ORE Command (yearly)</option>
                <option value="lifetime">C-ORE Sovereign (1 year)</option>
              </select>

              <label style={label}>Reason</label>
              <select data-testid="upgrade-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...input, marginBottom: 12 }}>
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>

              {reason === "other" && (
                <>
                  <label style={label}>Note (optional)</label>
                  <textarea data-testid="upgrade-reason-note" value={reasonNote} onChange={(e) => setReasonNote(e.target.value)}
                    placeholder="Add a short note…" style={{ ...input, height: 64, resize: "vertical", marginBottom: 12 }} />
                </>
              )}

              {!loadingCurrent && isDowngrade && (
                <div data-testid="upgrade-downgrade-warning" style={{ background: "#FEF3C7", color: "#92400E", fontSize: 12, padding: "8px 10px", borderRadius: 8, marginBottom: 12 }}>
                  This would downgrade the customer from {PLAN_LABELS[currentPlan]} to {PLAN_LABELS[target]}. Manual Upgrade only grants equal or higher plans.
                </div>
              )}

              {err && <div data-testid="upgrade-error" style={{ background: "#FEE2E2", color: "#B91C1C", fontSize: 12, padding: "8px 10px", borderRadius: 8, marginBottom: 12 }}>{err}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
                <button data-testid="upgrade-cancel-btn" onClick={onClose} disabled={busy}
                  style={{ ...btn, background: "white", color: "#374151", border: "1px solid #D1D5DB" }}>Cancel</button>
                <button data-testid="upgrade-confirm-btn" onClick={confirm} disabled={busy || loadingCurrent || isDowngrade}
                  style={{ ...btn, background: NAVY, color: "white", opacity: (busy || loadingCurrent || isDowngrade) ? 0.6 : 1 }}>
                  {busy ? "Applying…" : actionLabel(currentPlan, target)}
                </button>
              </div>
            </>
          )}

          {result && (
            <div data-testid="upgrade-success" style={{ textAlign: "center", padding: "12px 0" }}>
              <ArrowUpCircle size={28} color="#047857" />
              <div style={{ fontWeight: 700, color: "#0F1B2D", marginTop: 8 }}>
                {name} is now on {PLAN_LABELS[result.entitlement?.plan_code] || target}
              </div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
                {result.is_renewal ? "Renewed" : "Upgraded"} manually — entitlement active immediately.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function humanize(detail) {
  if (typeof detail === "string") {
    if (detail.startsWith("billing_migration_required"))
      return "Billing migration not applied yet. Run 2026-08_billing.sql and 2026-08_manual_upgrade.sql in Supabase first.";
    return detail;
  }
  const map = {
    invalid_target_plan: "Choose C-ORE Pulse, Command or Sovereign.",
    invalid_reason: "Choose a valid reason.",
    downgrade_not_allowed: "Manual Upgrade cannot move a customer to a lower plan.",
    customer_not_found: "Customer not found.",
  };
  const key = detail?.error || detail?.detail;
  return map[key] || detail?.hint || "Something went wrong.";
}
